/**
 * Firestore Product Master Price List — collection + field names (SQL import schema).
 */

export const PRODUCT_MASTER_COLLECTION = "Product Master Price List";

/** @param {Record<string, unknown>} row */
export function pickProductField(row, ...keys) {
  if (!row || typeof row !== "object") return undefined;
  for (const k of keys) {
    if (Object.prototype.hasOwnProperty.call(row, k)) {
      const v = row[k];
      if (v !== undefined && v !== null && String(v).trim() !== "") return v;
    }
  }
  return undefined;
}

function parseMoneyLike(v, fallback = 0) {
  if (v === undefined || v === null || v === "") return fallback;
  const n = Number(String(v).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : fallback;
}

const DEFAULT_GST = 18;

/**
 * Normalize Product Master doc → billing grid shape (Item Code = part no, Description = alias).
 * @param {import('firebase/firestore').QueryDocumentSnapshot | { id?: string, data?: () => object }} docSnap
 */
export function normalizeProductMasterDoc(docSnap) {
  const data = typeof docSnap?.data === "function" ? docSnap.data() || {} : docSnap || {};
  const id = docSnap?.id ?? pickProductField(data, "id") ?? "";

  const partno = String(
    pickProductField(data, "part no", "partno", "partNo", "Part No", "code") ?? ""
  ).trim();
  const alias = String(pickProductField(data, "alias", "Alias") ?? "").trim();
  const name = String(
    pickProductField(data, "item name", "itemname", "itemName", "name", "Name") ?? ""
  ).trim();

  const descValue =
    alias ||
    String(pickProductField(data, "desc", "description", "item description") ?? "").trim() ||
    name;

  const codeValue =
    partno ||
    String(pickProductField(data, "code", "item code") ?? "").trim() ||
    name ||
    String(pickProductField(data, "product id", "productid", "productId") ?? "").trim();

  const oprate = parseMoneyLike(pickProductField(data, "oprate", "OPRate"));
  const costprice = parseMoneyLike(
    pickProductField(data, "cost price", "costprice", "cost", "costPrice", "purcprice")
  );
  const mrpStored = parseMoneyLike(pickProductField(data, "mrp", "MRP", "price"));
  const mrp = mrpStored > 0 ? mrpStored : oprate > 0 ? oprate : 0;
  const rateFallback =
    parseMoneyLike(pickProductField(data, "rate", "Rate")) ||
    (oprate > 0 ? oprate : 0) ||
    (costprice > 0 ? costprice : 0);

  const productid = pickProductField(data, "product id", "productid", "productId", "productID");

  return {
    id,
    ...data,
    partno: partno || codeValue,
    alias: alias || descValue,
    name,
    code: codeValue,
    desc: descValue,
    productid,
    productId: productid,
    gst:
      Number(
        pickProductField(data, "outwardtax", "gst", "GST", "tax", "taxclassid", "taxClassId") ??
          DEFAULT_GST
      ) || DEFAULT_GST,
    rate: rateFallback,
    mrp,
    barcode: String(pickProductField(data, "barcode", "bar_code") ?? "").trim(),
    unit: String(
      pickProductField(data, "unit", "unitname", "unitName", "Unit") ?? "Nos"
    ).trim() || "Nos",
    brand: String(pickProductField(data, "brand", "brandName", "Brand") ?? "").trim(),
    group: String(pickProductField(data, "group name", "groupname", "group", "gname") ?? "").trim(),
    hsn: pickProductField(data, "hsn code", "hsncode", "hsnCode", "hsncodeid"),
    slNo: pickProductField(data, "sl_", "slNo", "slno"),
    stock: Number(pickProductField(data, "opstock", "stock", "max stock", "maxstock") ?? 0) || 0,
    cost: costprice,
    reorderLevel: Number(pickProductField(data, "reorder level", "reorderlevel") ?? 0) || 0,
  };
}

/** Firestore field names tried for equality / prefix search (new schema first). */
export const PRODUCT_PART_NO_FIELDS = ["part no", "partno", "code"];
export const PRODUCT_ID_FIELDS = ["product id", "productid", "productId"];
